import React from 'react';
import { FileText, Globe, Megaphone } from 'lucide-react';
import { Card } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { useTheme } from '../contexts/ThemeContext';

const PreviewPane = ({ conversation, brandVisualTheme, brandMeta }) => {
  const { resolvedTheme } = useTheme();
  const isBrandTheme = resolvedTheme === 'noderiver';
  const gradientBackground =
    brandVisualTheme?.modes?.noderiver?.heroGradient ??
    brandVisualTheme?.gradients?.hero ??
    'linear-gradient(135deg, #0B2433, #03131d)';

  // Get the latest message with preview
  const latestPreview = conversation?.messages
    ?.slice()
    .reverse()
    .find(msg => msg.preview);

  // Brand info for empty state
  const brandName = brandVisualTheme?.brandName ?? brandMeta?.name ?? 'BrandOn';
  const brandTagline = brandVisualTheme?.tagline ?? brandMeta?.industry ?? 'Your on-brand AI partner';
  const themeLogo = brandVisualTheme?.logosByMode?.[resolvedTheme] ?? brandVisualTheme?.logo;
  const brandLogo = themeLogo?.uri ?? '/noderiver.svg';
  const brandLogoAlt = themeLogo?.alt ?? `${brandName} logo`;

  if (!latestPreview?.preview) {
    return (
      <div
        className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-950"
        style={isBrandTheme ? { background: gradientBackground, color: '#ffffff' } : undefined}
      >
        <div className="text-center max-w-lg px-8">
          {/* Brand Logo */}
          <div 
            className={`w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-8 p-6 ${
              isBrandTheme ? 'bg-white/10 backdrop-blur-sm' : 'bg-gray-100 dark:bg-gray-800'
            }`}
            style={!isBrandTheme ? { 
              background: brandVisualTheme?.gradients?.hero ?? 'linear-gradient(135deg, #1E8997, #0e273a)' 
            } : undefined}
          >
            <img
              src={brandLogo}
              alt={brandLogoAlt}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          
          {/* Brand Name */}
          <h1 className={`text-4xl font-bold mb-3 ${isBrandTheme ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
            {brandName}
          </h1>
          
          {/* Tagline */}
          <p className={`text-lg mb-8 ${isBrandTheme ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
            {brandTagline}
          </p>
          
          {/* Helper text */}
          <div className={`text-sm ${isBrandTheme ? 'text-white/50' : 'text-gray-400 dark:text-gray-500'}`}>
            <p>Content previews will appear here as the agent generates</p>
            <p>emails, website updates, and marketing copy.</p>
          </div>
        </div>
      </div>
    );
  }

  const { preview } = latestPreview;

  const renderEmailPreview = () => (
    <div className={`h-full ${isBrandTheme ? 'bg-white/5 text-white border border-white/10' : 'bg-white dark:bg-gray-900'}`}>
      <div className={`border-b px-6 py-4 ${isBrandTheme ? 'border-white/10' : 'border-gray-200 dark:border-gray-800'}`}>
        <div className={`flex items-center space-x-2 mb-3 ${isBrandTheme ? 'text-white/80' : 'text-gray-500 dark:text-gray-300'}`}>
          <FileText className="w-4 h-4" />
          <span className="text-sm font-medium">Email Preview</span>
        </div>
        <div className="space-y-2">
          <div className="flex items-baseline">
            <span className={`text-xs font-medium w-16 ${isBrandTheme ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>Subject:</span>
            <span className={`text-sm font-medium ${isBrandTheme ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{preview.subject}</span>
          </div>
        </div>
      </div>
      <ScrollArea className="px-6 py-4 h-[calc(100%-100px)]">
        <div className="prose prose-sm max-w-none">
          <pre className={`whitespace-pre-wrap font-sans text-sm leading-relaxed ${isBrandTheme ? 'text-white/90' : 'text-gray-700 dark:text-gray-200'}`}>
            {preview.body}
          </pre>
        </div>
      </ScrollArea>
    </div>
  );

  const renderWebsitePreview = () => (
    <div className={`h-full ${isBrandTheme ? 'bg-white/5 border border-white/10 text-white' : 'bg-white dark:bg-gray-900'}`}>
      <div className={`border-b px-6 py-4 ${isBrandTheme ? 'border-white/10' : 'border-gray-200 dark:border-gray-800'}`}>
        <div className={`flex items-center space-x-2 ${isBrandTheme ? 'text-white/80' : 'text-gray-500 dark:text-gray-300'}`}>
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">Website Preview</span>
        </div>
      </div>
      <ScrollArea className="p-6 h-[calc(100%-70px)]">
        <div className={`rounded-lg overflow-hidden ${isBrandTheme ? 'bg-white' : 'border border-gray-200 dark:border-gray-800'}`}>
          <div 
            dangerouslySetInnerHTML={{ __html: preview.html }}
          />
        </div>
        <div className={`mt-4 p-4 rounded-lg ${isBrandTheme ? 'bg-white/10 text-white/80' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}>
          <p className="text-xs">
            <strong>Note:</strong> This is a preview of the generated content. You can refine it by providing additional feedback to the agent.
          </p>
        </div>
      </ScrollArea>
    </div>
  );

  const renderMarketingPreview = () => (
    <div className={`h-full ${isBrandTheme ? 'bg-white/5 border border-white/10 text-white' : 'bg-white dark:bg-gray-900'}`}>
      <div className={`border-b px-6 py-4 ${isBrandTheme ? 'border-white/10' : 'border-gray-200 dark:border-gray-800'}`}>
        <div className={`flex items-center space-x-2 ${isBrandTheme ? 'text-white/80' : 'text-gray-500 dark:text-gray-300'}`}>
          <Megaphone className="w-4 h-4" />
          <span className="text-sm font-medium">Marketing Copy Preview</span>
        </div>
      </div>
      <ScrollArea className="px-6 py-4 h-[calc(100%-70px)]">
        <div className="space-y-4">
          {preview.copies?.map((copy, idx) => (
            <Card
              key={idx}
              className={`p-4 ${isBrandTheme ? 'bg-white/5 border-white/10 text-white' : ''}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`text-sm font-semibold ${isBrandTheme ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                  {copy.platform}
                </span>
                <span className={`text-xs ${isBrandTheme ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                  {copy.metrics}
                </span>
              </div>
              <div className={`text-sm whitespace-pre-wrap leading-relaxed ${isBrandTheme ? 'text-white/90' : 'text-gray-700 dark:text-gray-200'}`}>
                {copy.content}
              </div>
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <div
      className="h-full bg-gray-50 dark:bg-gray-950"
      style={isBrandTheme ? { background: gradientBackground, color: '#ffffff' } : undefined}
    >
      {preview.type === 'email' && renderEmailPreview()}
      {preview.type === 'website' && renderWebsitePreview()}
      {preview.type === 'marketing' && renderMarketingPreview()}
    </div>
  );
};

export default PreviewPane;
